import React, { useMemo, useState } from "react";
import { FiTrendingUp, FiUsers, FiHeadphones } from "react-icons/fi";
import PaginationBar from "../../components/common/PaginationBar";
import { CardsSkeleton, TableSkeleton } from "../../components/common/skeletons";
import SupportPerformanceTicketsModal from "./SupportPerformanceTicketsModal";

const THEME = { primary: "#FB565F" };
const PAGE_SIZE = 10;

const STATUS_COLUMNS = [
  { key: "PENDING", label: "Pending", badge: "bg-amber-100 text-amber-800 hover:ring-amber-300" },
  { key: "IN_PROGRESS", label: "In Progress", badge: "bg-blue-100 text-blue-800 hover:ring-blue-300" },
  { key: "COMPLETED", label: "Completed", badge: "bg-purple-100 text-purple-800 hover:ring-purple-300" },
  { key: "RESOLVED", label: "Resolved", badge: "bg-emerald-100 text-emerald-800 hover:ring-emerald-300" },
  { key: "CLOSED", label: "Closed", badge: "bg-gray-100 text-gray-700 hover:ring-gray-300" },
];

const ROLE_FILTER_OPTIONS = [
  { value: "all", label: "All roles" },
  { value: "CSR", label: "CSR only" },
  { value: "CSR Team Lead", label: "CSR Team Lead only" },
  { value: "Chat Support", label: "Chat Support only" },
  { value: "former", label: "Former CSR (historical)" },
];

const SORT_OPTIONS = [
  { value: "total_desc", label: "Total handled (high → low)" },
  { value: "total_asc", label: "Total handled (low → high)" },
  { value: "name_asc", label: "Name (A → Z)" },
  { value: "name_desc", label: "Name (Z → A)" },
  { value: "resolved_desc", label: "Resolved rate (high → low)" },
];

function CountBadge({ value, className, onClick }) {
  const clickable = value > 0 && onClick;
  const Tag = clickable ? "button" : "span";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      title={clickable ? "View tickets" : undefined}
      className={`inline-flex min-w-[2rem] justify-center px-2 py-0.5 rounded-full text-xs font-bold transition-all ${
        clickable ? "cursor-pointer hover:ring-2 hover:ring-offset-1" : ""
      } ${className}`}
    >
      {value}
    </Tag>
  );
}

function resolutionRate(counts) {
  const total = Number(counts?.total) || 0;
  if (!total) return 0;
  const done =
    (Number(counts.RESOLVED) || 0) +
    (Number(counts.CLOSED) || 0) +
    (Number(counts.COMPLETED) || 0);
  return Math.round((done / total) * 100);
}

function roleSummaryFor(list) {
  const groups = {};
  for (const p of list) {
    const role = p.panelRole || "CSR";
    if (!groups[role]) {
      groups[role] = { role, agents: 0, tickets: 0 };
    }
    groups[role].agents += 1;
    groups[role].tickets += Number(p.counts?.total) || 0;
  }
  return Object.values(groups).sort((a, b) => b.tickets - a.tickets);
}

export default function SupportPerformanceTab({
  performance = [],
  loading,
  scopeLabel,
  viewScope = "all",
  performanceKind = "csr",
  isChatSupport = false,
  ledTeams = [],
  selectedTeamId = null,
  onTeamSelect,
  onViewTicket,
  isAdmin = false,
}) {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("total_desc");
  const [minTotal, setMinTotal] = useState("");
  const [page, setPage] = useState(1);
  const [ticketModal, setTicketModal] = useState({ open: false, agent: null, status: "" });

  const openTicketModal = (agent, status = "") => {
    setTicketModal({
      open: true,
      agent: {
        agentId: agent.agentId,
        name: agent.name,
        email: agent.email,
        panelRole: agent.panelRole,
        teamName: agent.teamName,
        isTeamLead: agent.isTeamLead,
      },
      status,
    });
  };

  const closeTicketModal = () => {
    setTicketModal({ open: false, agent: null, status: "" });
  };

  const isChatTeamView = performanceKind === "chat_support" || (isChatSupport && !isAdmin);
  const isSelfView = viewScope === "self" || (isChatSupport && !isAdmin && !ledTeams.length);
  const isAdminAllView = isAdmin && (performanceKind === "all" || (!selectedTeamId && viewScope === "all"));
  const showSaleOutreachOnly =
    !isChatTeamView &&
    !isSelfView &&
    !isAdminAllView &&
    performanceKind === "csr" &&
    Boolean(selectedTeamId || (ledTeams.length > 0 && !isAdmin));

  const csrRows = useMemo(() => {
    const rows = performance.filter(
      (p) =>
        p.isTeamLead ||
        p.panelRole === "CSR" ||
        p.panelRole === "CSR Team Lead" ||
        (p.csrOutreachCounts?.total || 0) > 0
    );
    return [...rows].sort((a, b) => {
      if (a.isTeamLead && !b.isTeamLead) return -1;
      if (!a.isTeamLead && b.isTeamLead) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [performance]);

  const ticketRows = useMemo(() => {
    if (isChatTeamView || isSelfView) return performance;
    if (showSaleOutreachOnly) return csrRows;
    return performance;
  }, [isChatTeamView, isSelfView, showSaleOutreachOnly, performance, csrRows]);

  const roleScoped = useMemo(() => {
    const base = ticketRows;
    if (isSelfView || roleFilter === "all") return base;
    if (roleFilter === "former") return base.filter((p) => p.isFormerCsr);
    return base.filter((p) => p.panelRole === roleFilter);
  }, [roleFilter, ticketRows, isSelfView]);

  const summary = useMemo(() => {
    const agents = roleScoped.length;
    if (isChatTeamView) {
      const totalTickets = roleScoped.reduce((s, p) => s + (Number(p.counts?.total) || 0), 0);
      const avgTickets = agents ? Math.round(totalTickets / agents) : 0;
      const resolvedRates = roleScoped.map((p) => resolutionRate(p.counts));
      const avgResolved =
        resolvedRates.length > 0
          ? Math.round(resolvedRates.reduce((s, r) => s + r, 0) / resolvedRates.length)
          : 0;
      return {
        agents,
        totalHandled: totalTickets,
        avgPerAgent: isSelfView ? avgResolved : avgTickets,
      };
    }
    if (showSaleOutreachOnly) {
      const totalOutreach = roleScoped.reduce(
        (s, p) => s + (Number(p.csrOutreachCounts?.total) || 0),
        0
      );
      const avgPerAgent = agents ? Math.round(totalOutreach / agents) : 0;
      return { agents, totalHandled: totalOutreach, avgPerAgent };
    }
    const totalHandled = roleScoped.reduce((s, p) => s + (Number(p.counts?.total) || 0), 0);
    const avgPerAgent = agents ? Math.round(totalHandled / agents) : 0;
    return { agents, totalHandled, avgPerAgent };
  }, [roleScoped, isChatTeamView, showSaleOutreachOnly, isSelfView]);

  const roleBreakdown = useMemo(() => roleSummaryFor(performance), [performance]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const min = minTotal === "" ? null : Number(minTotal);
    let list = [...roleScoped];

    if (q) {
      list = list.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const email = (p.email || "").toLowerCase();
        const team = (p.teamName || "").toLowerCase();
        const role = (p.panelRole || "").toLowerCase();
        return name.includes(q) || email.includes(q) || team.includes(q) || role.includes(q);
      });
    }

    if (min != null && Number.isFinite(min)) {
      list = list.filter((p) => (Number(p.counts?.total) || 0) >= min);
    }

    list.sort((a, b) => {
      const at = Number(a.counts?.total) || 0;
      const bt = Number(b.counts?.total) || 0;
      const an = (a.name || "").toLowerCase();
      const bn = (b.name || "").toLowerCase();
      const ar = resolutionRate(a.counts);
      const br = resolutionRate(b.counts);
      switch (sortBy) {
        case "total_asc":
          return at - bt;
        case "name_asc":
          return an.localeCompare(bn);
        case "name_desc":
          return bn.localeCompare(an);
        case "resolved_desc":
          return br - ar || bt - at;
        default:
          return bt - at;
      }
    });

    return list;
  }, [roleScoped, search, sortBy, minTotal]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setSortBy("total_desc");
    setMinTotal("");
    setPage(1);
  };

  const hasFilters =
    search.trim() || roleFilter !== "all" || minTotal !== "" || sortBy !== "total_desc";

  const inputCls =
    "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none";

  if (loading) {
    return (
      <div className="space-y-5">
        <CardsSkeleton count={3} cols="grid-cols-1 sm:grid-cols-3" />
        <TableSkeleton rows={6} cols={7} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {(ledTeams.length > 1 || (isAdmin && ledTeams.length > 0)) && (
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
          {isAdmin && (
            <button
              type="button"
              onClick={() => onTeamSelect?.(null)}
              className={`inline-flex items-center gap-2 rounded-t-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                !selectedTeamId
                  ? "border-gray-200 border-b-white bg-white text-gray-900 shadow-sm -mb-px"
                  : "border-transparent bg-gray-50 text-gray-600 hover:bg-gray-100"
              }`}
            >
              All teams
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-700">
                Everyone
              </span>
            </button>
          )}
          {ledTeams.map((team) => {
            const active = selectedTeamId != null && Number(selectedTeamId) === Number(team.id);
            return (
              <button
                key={team.id}
                type="button"
                onClick={() => onTeamSelect?.(team.id)}
                className={`inline-flex items-center gap-2 rounded-t-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? "border-gray-200 border-b-white bg-white text-gray-900 shadow-sm -mb-px"
                    : "border-transparent bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {team.name}
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    team.teamType === "chat_support"
                      ? "bg-cyan-100 text-cyan-800"
                      : "bg-teal-100 text-teal-800"
                  }`}
                >
                  {team.teamType === "chat_support" ? "Chat" : "CSR"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-red-50/40 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiUsers className="text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {isSelfView ? "Your profile" : "Support agents"}
              </p>
              <p className="text-2xl font-bold text-gray-900">{isSelfView ? 1 : summary.agents}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/40 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white">
              <FiHeadphones className="text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {isChatTeamView
                  ? isSelfView
                    ? "Your tickets"
                    : "Tickets handled"
                  : showSaleOutreachOnly
                    ? "Outreach customers"
                    : "Tickets handled"}
              </p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalHandled}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-emerald-50/40 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white">
              <FiTrendingUp className="text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {isChatTeamView
                  ? isSelfView
                    ? "Resolved rate"
                    : "Avg tickets / agent"
                  : showSaleOutreachOnly
                    ? "Avg outreach / agent"
                    : "Avg per agent"}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {isChatTeamView && isSelfView ? `${summary.avgPerAgent}%` : summary.avgPerAgent}
              </p>
            </div>
          </div>
        </div>
      </div>

      {roleBreakdown.length > 1 && roleFilter === "all" && !isChatTeamView && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Performance by role
          </p>
          <div className="flex flex-wrap gap-3">
            {roleBreakdown.map((group) => (
              <button
                key={group.role}
                type="button"
                onClick={() => {
                  setRoleFilter(group.role);
                  setPage(1);
                }}
                className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-left hover:border-red-200 hover:bg-red-50/30 transition-colors min-w-[160px]"
              >
                <p className="text-xs font-semibold text-gray-700">{group.role}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{group.tickets} tickets</p>
                <p className="text-[11px] text-gray-500">{group.agents} agent{group.agents !== 1 ? "s" : ""}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {scopeLabel && (
        <p className="text-xs text-gray-500 px-1">
          Showing: <span className="font-semibold text-gray-700">{scopeLabel}</span>
          {roleFilter !== "all" && (
            <>
              {" "}
              · Role: <span className="font-semibold text-gray-700">{roleFilter}</span>
            </>
          )}
        </p>
      )}

      {showSaleOutreachOnly ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-teal-50/50">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Sale / CSR outreach performance
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Agent</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Total</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Pending</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Contacted</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Completed</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Resolved</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Calls/mo</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Users messaged/mo</th>
                </tr>
              </thead>
              <tbody>
                {csrRows.length === 0 ? (
                  <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      No sale / CSR outreach data for this team yet.
                    </td>
                  </tr>
                ) : (
                  csrRows.map((p) => (
                    <tr key={p.agentId} className="border-b border-gray-100 hover:bg-teal-50/20">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{p.name || "—"}</p>
                        <p className="text-xs text-gray-500">
                          {p.isTeamLead ? "Team Lead · " : ""}
                          {p.email || "—"}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-center font-bold text-gray-800">
                        {p.csrOutreachCounts?.total || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.csrOutreachCounts?.pending || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.csrOutreachCounts?.contacted || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.csrOutreachCounts?.completed || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.csrOutreachCounts?.resolved || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.monthlyCalls || 0}
                      </td>
                      <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                        {p.monthlyMessages || 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          {!isSelfView && (
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Search</label>
                  <input
                    type="text"
                    placeholder="Name, email, team, or role…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className={`${inputCls} w-full`}
                  />
                </div>
                {!isChatTeamView && (
                  <div className="min-w-[180px]">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Role</label>
                    <select
                      value={roleFilter}
                      onChange={(e) => {
                        setRoleFilter(e.target.value);
                        setPage(1);
                      }}
                      className={`${inputCls} w-full`}
                    >
                      {ROLE_FILTER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="min-w-[200px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`${inputCls} w-full`}
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-36">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Min. tickets</label>
                  <input
                    type="number"
                    min={0}
                    placeholder="0"
                    value={minTotal}
                    onChange={(e) => {
                      setMinTotal(e.target.value);
                      setPage(1);
                    }}
                    className={`${inputCls} w-full`}
                  />
                </div>
                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Click any ticket count to open a popup and view tickets by status.
              </p>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {isChatTeamView && (
              <div className="px-4 py-3 border-b border-gray-100 bg-cyan-50/50">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {isSelfView ? "Your ticket performance" : "Chat support ticket performance"}
                </p>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide min-w-[180px]">
                      Agent
                    </th>
                    {!isSelfView && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Team</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Role</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide">Total</th>
                    {!isChatTeamView && (
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                        CSR Outreach
                      </th>
                    )}
                    {STATUS_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {col.label}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Users/day
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Users/mo
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Calls/mo
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Users messaged/mo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Resolved %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isSelfView ? 13 : isChatTeamView ? 15 : 16}
                        className="px-4 py-16 text-center text-gray-500"
                      >
                        {performance.length === 0
                          ? isChatTeamView
                            ? "No chat support performance data yet."
                            : "No support performance data yet. Stats appear when agents handle tickets or CSR outreach."
                          : "No agents match your filters."}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((p, idx) => {
                      const c = p.counts || {};
                      const rate = resolutionRate(c);
                      const rowNum = (safePage - 1) * PAGE_SIZE + idx + 1;
                      return (
                        <tr
                          key={p.agentId}
                          className="border-b border-gray-100 hover:bg-red-50/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-400 font-medium tabular-nums">{rowNum}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                                style={{ backgroundColor: THEME.primary }}
                              >
                                {(p.name || "C").charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{p.name || "—"}</p>
                                <p className="text-xs text-gray-500 truncate">{p.email || "—"}</p>
                              </div>
                            </div>
                          </td>
                          {!isSelfView && (
                            <>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                {p.teamName || "—"}
                                {p.isTeamLead && (
                                  <span className="ml-1 text-[10px] font-bold text-indigo-600">(Lead)</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                                    {p.panelRole || "CSR"}
                                  </span>
                                  {p.isFormerCsr && (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                      Former CSR
                                    </span>
                                  )}
                                </div>
                              </td>
                            </>
                          )}
                          <td className="px-4 py-3 text-center">
                            <CountBadge
                              value={c.total || 0}
                              className="min-w-[2.5rem] px-2.5 py-1 rounded-lg bg-gray-900 text-white text-sm"
                              onClick={c.total > 0 ? () => openTicketModal(p, "") : undefined}
                            />
                          </td>
                          {!isChatTeamView && (
                            <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                              {p.csrOutreachCounts?.total || 0}
                            </td>
                          )}
                          {STATUS_COLUMNS.map((col) => (
                            <td key={col.key} className="px-3 py-3 text-center">
                              <CountBadge
                                value={c[col.key] || 0}
                                className={col.badge}
                                onClick={
                                  (c[col.key] || 0) > 0
                                    ? () => openTicketModal(p, col.key)
                                    : undefined
                                }
                              />
                            </td>
                          ))}
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.dailyUsersResponded || 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.monthlyUsersResponded || 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.monthlyCalls || 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.monthlyMessages || 0}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-bold text-gray-800">{rate}%</span>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${rate}%`,
                                    backgroundColor: rate >= 70 ? "#10b981" : rate >= 40 ? "#f59e0b" : THEME.primary,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!isSelfView && (
              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                label="agents"
                onPageChange={setPage}
              />
            )}
          </div>

          {isChatTeamView && (
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-cyan-50/50">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Chat support log performance (this month)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Agent</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Customers</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Target %</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Chats</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Emails</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Social</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-500 uppercase">Random</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          No chat support log data for this period yet.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((p) => (
                        <tr key={p.agentId} className="border-b border-gray-100 hover:bg-cyan-50/20">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-900">{p.name || "—"}</p>
                            <p className="text-xs text-gray-500">
                              {p.isTeamLead ? "Team Lead · " : ""}
                              {p.email || "—"}
                            </p>
                          </td>
                          <td className="px-3 py-3 text-center font-bold text-gray-800">
                            {p.chatSupportLog?.customerLogCount ?? 0}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex min-w-[2.5rem] justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-cyan-100 text-cyan-800">
                              {p.chatSupportLog?.overallPercent ?? 0}%
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.chatSupportLog?.totals?.chatMessages ?? 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.chatSupportLog?.totals?.emailsAdded ?? 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.chatSupportLog?.totals?.socialResponses ?? 0}
                          </td>
                          <td className="px-3 py-3 text-center text-xs font-semibold text-gray-700">
                            {p.chatSupportLog?.totals?.randomRequests ?? 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <SupportPerformanceTicketsModal
        key={
          ticketModal.open
            ? `${ticketModal.agent?.agentId}-${ticketModal.status || "all"}`
            : "closed"
        }
        open={ticketModal.open}
        agent={ticketModal.agent}
        status={ticketModal.status}
        onClose={closeTicketModal}
        onViewTicket={onViewTicket}
        canViewClosedChats={isAdmin}
      />
    </div>
  );
}
